import { useEffect, useState } from "react";
import { Play } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSectionMeta } from "@/context/SiteConfigContext";
import { api, assetUrl } from "@/lib/api";
import { ytThumb, ytEmbed } from "@/lib/youtube";
import { SectionHead, MaskWords, SectionButtons } from "@/components/site/primitives";
import { Reveal } from "@/components/site/Reveal";

export default function HomeCourses() {
  const { lang, t } = useLang();
  const c = t.home_courses || {};
  const meta = useSectionMeta("home_courses");
  const [courses, setCourses] = useState([]);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data.slice(0, 3))).catch(() => setCourses([]));
  }, []);

  const g = (x, k) => x[`${k}_${lang}`] || x[`${k}_ro`] || "";
  if (!courses.length) return null;

  return (
    <section
      id="home_courses"
      data-testid="home-courses"
      className="bg-paper text-ink px-6 md:px-12 lg:px-20 py-28 md:py-40"
    >
      <SectionHead label={c.label} index="" />
      <div className="mt-10 md:mt-14 grid grid-cols-12 gap-6 items-end mb-14 md:mb-20">
        <MaskWords
          Tag="h2"
          text={c.title}
          className="col-span-12 lg:col-span-8 display uppercase text-[12vw] lg:text-[8vw] leading-[0.85] break-words"
        />
        <p className="col-span-12 lg:col-span-4 text-ink/65 leading-[1.6]">{c.intro}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
        {courses.map((cr, i) => {
          const cover = assetUrl(cr.cover_image) || ytThumb(cr.youtube_url);
          return (
            <Reveal key={cr.id} delay={(i % 2) * 0.08}>
              {playing === cr.id && ytEmbed(cr.youtube_url) ? (
                <div>
                  <div className="relative overflow-hidden aspect-video bg-black" data-testid={`home-course-inline-${i}`}>
                    <iframe
                      key={cr.id}
                      src={ytEmbed(cr.youtube_url, true)}
                      title={g(cr, "title")}
                      className="absolute inset-0 w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <div className="pt-4">
                    <div className="eyebrow text-ink/45 mb-1">{g(cr, "subtitle")}</div>
                    <h3 className="serif text-2xl md:text-3xl leading-[1.1]">{g(cr, "title")}</h3>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => cr.youtube_url && setPlaying(cr.id)}
                  data-testid={`home-course-${i}`}
                  data-cursor="link"
                  className="group text-left w-full"
                >
                  <div className="relative overflow-hidden aspect-video bg-ink/5">
                    {cover && (
                      <img
                        src={cover}
                        alt={g(cr, "title")}
                        onError={(e) => { if (e.currentTarget.src.includes("maxresdefault")) e.currentTarget.src = e.currentTarget.src.replace("maxresdefault", "mqdefault"); }}
                        className="w-full h-full object-contain"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="w-16 h-16 rounded-full border border-white/60 text-white flex items-center justify-center backdrop-blur-sm transition-all duration-500 group-hover:bg-white group-hover:text-black">
                        <Play size={20} className="ml-0.5" fill="currentColor" />
                      </span>
                    </span>
                  </div>
                  <div className="pt-4">
                    <div className="eyebrow text-ink/45 mb-1">{g(cr, "subtitle")}</div>
                    <h3 className="serif text-2xl md:text-3xl leading-[1.1]">{g(cr, "title")}</h3>
                  </div>
                </button>
              )}
            </Reveal>
          );
        })}
      </div>

      <div className="mt-12 md:mt-16">
        <SectionButtons buttons={meta.buttons} />
      </div>
    </section>
  );
}
